// One-click toggle that adds / removes an opportunity from the operator's
// pipeline. Shares the /me/relations probe with TrackButton so both can
// render initial state from a single round-trip per detail page.
//
// Pipeline membership uses the auto-created `discovered` stage. Stage
// management (move / re-prioritise / kanban) is a future Pipeline UI; the
// button only handles the membership relation needed by the brief's
// on-your-radar strip.
//
// Only valid for tender / award / standing_offer entity types — pipeline_items
// is constrained to opportunities, not vendors / agencies.

import { apiFetch } from '@/lib/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

type PipelineEntityType = 'tender' | 'award' | 'standing_offer';

interface RelationsResponse {
  tracked: boolean;
  in_pipeline: boolean;
}

interface AddToPipelineButtonProps {
  entityType: PipelineEntityType;
  entityId: string;
}

function relationsKey(entityType: string, entityId: string): readonly unknown[] {
  return ['me', 'relations', entityType, entityId] as const;
}

export function AddToPipelineButton({ entityType, entityId }: AddToPipelineButtonProps) {
  const qc = useQueryClient();
  const key = relationsKey(entityType, entityId);

  const rel = useQuery({
    queryKey: key,
    enabled: !!entityId,
    queryFn: () =>
      apiFetch<RelationsResponse>(
        `/v1/me/relations?entity_type=${encodeURIComponent(entityType)}&entity_id=${encodeURIComponent(entityId)}`,
      ),
    staleTime: 30_000,
  });

  const inPipeline = rel.data?.in_pipeline ?? false;

  const mutate = useMutation({
    mutationFn: async (next: boolean) => {
      // apiFetch JSON.stringifies the body itself + sets content-type.
      // Pre-stringifying double-encoded the payload → 400.
      await apiFetch('/v1/me/pipeline', {
        method: next ? 'POST' : 'DELETE',
        body: { entity_type: entityType, entity_id: entityId },
      });
    },
    onMutate: async (next) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<RelationsResponse>(key);
      qc.setQueryData<RelationsResponse>(key, {
        tracked: prev?.tracked ?? false,
        in_pipeline: next,
      });
      return { prev };
    },
    onError: (_err, _next, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: key });
      void qc.invalidateQueries({ queryKey: ['brief'] });
    },
  });

  if (rel.isLoading) {
    return (
      <button type="button" className="btn xs gh" disabled>
        + PIPELINE
      </button>
    );
  }

  return (
    <button
      type="button"
      className={inPipeline ? 'btn xs primary' : 'btn xs gh'}
      onClick={() => mutate.mutate(!inPipeline)}
      disabled={mutate.isPending}
      aria-pressed={inPipeline}
      title={
        inPipeline
          ? 'Remove from pipeline'
          : 'Add to pipeline — surfaces in My Brief radar within 14 days'
      }
    >
      {inPipeline ? 'IN PIPELINE' : '+ PIPELINE'}
    </button>
  );
}
